import React, { FunctionComponent } from 'react';

import ResponsiveHeader from '@gojob/ui-bits/ResponsiveHeader';
import Divider from '@gojob/ui-bits/ResponsiveHeader/Divider';
import HeaderLink from '@gojob/ui-bits/ResponsiveHeader/Link';
import TranslationProvider from '@gojob/ui-bits/ResponsiveHeader/TranslationProvider';
import { graphql } from 'gatsby';

import useDefaultHeader from 'src/components/Header/useDefaultHeader';
import useTranslation from 'src/translations/useTranslation';
import { Universe } from 'src/types/gojob';
import normalizeLink from 'src/utils/normalize-link';

export const HeaderFragment = graphql`
  fragment HeaderFragment on SanityUniverse {
    logo {
      id: html_id
      ariaLabel: aria_label
      internalRef: ref {
        slug {
          current
        }
      }
    }
    items: item {
      __typename
      ... on SanityHeaderLink {
        id: html_id
        children: label
        href
        featured
        internalRef: ref {
          ... on SanityPage {
            slug {
              current
            }
          }
          ... on SanityArticle {
            slug {
              current
            }
          }
          ... on SanityCategory {
            slug {
              current
            }
          }
        }
        icon {
          type
        }
      }
    }
    sibling_universe {
      id: html_id
      children: label
      internalRef: ref {
        root_page {
          slug {
            current
          }
        }
      }
      icon {
        type
      }
    }
  }
`;

export type HeaderProps = {
  universe?: Universe;
};

const renderItem = (item?: Required<Universe>['items'][0], index?: number) => {
  switch (item?.__typename) {
    case 'SanityHeaderLink':
      const { internalRef, featured, href, icon } = item;
      const url = normalizeLink(internalRef?.slug?.current || href);
      return (
        <HeaderLink key={index} newFeature={featured} as="a" href={url} icon={icon?.type}>
          {item?.children}
        </HeaderLink>
      );
    case 'SanityDivider':
      return <Divider key={index} />;
    default:
      return null;
  }
};

const Header: FunctionComponent<HeaderProps> = () => {
  const t = useTranslation();

  const universe = useDefaultHeader();
  const { items, logo, sibling_universe } = universe;

  const logoHref = normalizeLink(logo?.internalRef?.slug?.current);

  return (
    <TranslationProvider translationFunction={t}>
      <ResponsiveHeader
        /** Sadly we need to cast this to any as Link does not explicitely state this key in it's props */
        LogoProps={{ as: 'a', href: logoHref, 'aria-label': logo?.ariaLabel } as any}
      >
        {items?.map(renderItem)}
        {universe?.sibling_universe ? (
          <HeaderLink
            as={'a'}
            variant="primary"
            href={normalizeLink(sibling_universe?.internalRef?.root_page?.slug?.current)}
            icon={sibling_universe?.icon?.type}
          >
            {sibling_universe?.children}
          </HeaderLink>
        ) : null}
      </ResponsiveHeader>
    </TranslationProvider>
  );
};

export default Header;
